import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { WorkbookMapper } from '../../ts/mappers/WorkbookMapper';
import { WorkbookContentType } from '../../ts/models/ContentTypes';
import { ITaggableContent } from '../../ts/models/ITaggableContent';
import { IWorkbookDetailInfo, IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { AddTagsToWorkbooksRequest } from '../../ts/ServerApi/requests/AddTagsToWorkbooksRequest';
import { CancelExtractCreationRequest } from '../../ts/ServerApi/requests/CancelExtractCreationRequest';
import { CreateExtractTasksRequest } from '../../ts/ServerApi/requests/CreateExtractTasksRequest';
import { DeleteWorkbooksRequest } from '../../ts/ServerApi/requests/DeleteWorkbooksRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetWorkbookActionsRequest } from '../../ts/ServerApi/requests/GetWorkbookActionsRequest';
import { GetWorkbooksRequest } from '../../ts/ServerApi/requests/GetWorkbooksRequest';
import {
  IIsWorkbookServerRefreshableRequest,
  IIsWorkbookServerRefreshableResult,
} from '../../ts/ServerApi/requests/IsWorkbookServerRefreshable';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { MoveWorkbooksToProjectRequest } from '../../ts/ServerApi/requests/MoveWorkbooksToProjectRequest';
import { PerformExtractEncryptionActionOnWorkbooksRequest } from '../../ts/ServerApi/requests/PerformExtractEncryptionActionOnWorkbooksRequest';
import { RemoveTagsFromWorkbooksRequest } from '../../ts/ServerApi/requests/RemoveTagsFromWorkbooksRequest';
import { RenameWorkbookRequest } from '../../ts/ServerApi/requests/RenameWorkbookRequest';
import { IRevertWorkbookResult, RevertWorkbookRequest } from '../../ts/ServerApi/requests/RevertWorkbookRequest';
import { RunCreateExtractsOnWorkbooksRequest } from '../../ts/ServerApi/requests/RunCreateExtractsOnWorkbooksRequest';
import { RunExtractRefreshesOnWorkbooksRequest } from '../../ts/ServerApi/requests/RunExtractRefreshesOnWorkbooksRequest';
import { SetDisplayTabsRequest } from '../../ts/ServerApi/requests/SetDisplayTabsRequest';
import { SetWorkbookDescriptionRequest } from '../../ts/ServerApi/requests/SetWorkbookDescriptionRequest';
import { SetWorkbooksOwnerRequest } from '../../ts/ServerApi/requests/SetWorkbooksOwnerRequest';
import { UnencryptedConnectionKeychain } from '../../ts/ServerApi/types/IConnectionKeyChain';
import { ExtractTaskTargetType } from '../../ts/ServerApi/types/IExtractTask';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { IWorkbookActions } from '../../ts/ServerApi/types/IWorkbookActions';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { EnableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/EnableWorkbookAccelerationRequest';
import { DisableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/DisableWorkbookAccelerationRequest';
import { GetExtractDraftIdRequest } from '../../ts/ServerApi/requests/GetExtractDraftIdRequest';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';

export class WorkbooksResource implements IListResource<IWorkbookInfo>, IFieldValuesResource, IDetailResource<IWorkbookDetailInfo>, IActionResource<IWorkbookActions>, ITaggableContent {

  private listFetcher: IListFetcher<IWorkbookInfo>;
  private allQuery: ResourceQuery<IWorkbookInfo>;
  private detailFetcher: IDetailFetcher<IWorkbookDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetWorkbooksRequest, ServerApiMapper.workbooks);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = new WebClientDetailFetcher('getWorkbook', WorkbookMapper.mapDetail);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public isWorkbookInfo(item: any): item is IWorkbookInfo {
    return item &&
           item.contentType &&
           item.contentType === 'workbook' &&
           item.hasOwnProperty('numSheets'); // this field exists in IWorkbookInfo and not IWorkbookDetailInfo
  }

  public isWorkbookDetailInfo(item: any): item is IWorkbookDetailInfo {
    return item &&
      item.contentType &&
      item.contentType === 'workbook' &&
      item.hasOwnProperty('description'); // this field exists in IWorkbookDetailInfo and not IWorkbookInfo
  }

  public all(): ResourceQuery<IWorkbookInfo> {
    return this.allQuery;
  }

  public getById(id: string): Promise<IWorkbookDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'workbook');
  }

  // Actions

  public actions(id: string): Promise<IWorkbookActions> {
    return WebHttpClient.sendRequest(new GetWorkbookActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteWorkbooksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RenameWorkbookRequest(id, name));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetWorkbookDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetWorkbooksOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, workbookIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(workbookIds,
        (idsBlock) => new MoveWorkbooksToProjectRequest({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public setDisplayTabs(ids: string[], displayTabs: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new SetDisplayTabsRequest({
          ids: idsBlock,
          displayTabs: displayTabs
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public revertWorkbook(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): Promise<IRevertWorkbookResult> {
    const request = new RevertWorkbookRequest({
      id: id,
      version: version,
      keychains: keychains,
      encryptedPasswordKeyId: encryptedPasswordKeyId
    });
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(request);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public createExtractTask(ids: string[], scheduleId: string, extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new CreateExtractTasksRequest({
          targetType: ExtractTaskTargetType.Workbook,
          targetIds: idsBlock,
          scheduleId: scheduleId,
          type: extractType // ExtractTaskType
        })),
      ResultHelper.combineResults);
  }

  public enableWorkbookAcceleration(ids: string[], scheduleId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new EnableWorkbookAccelerationRequest({
          targetIds: idsBlock,
          scheduleId: scheduleId
        })),
      ResultHelper.combineResults);
  }

  public disableWorkbookAcceleration(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new DisableWorkbookAccelerationRequest({
          targetIds: idsBlock
        })),
      ResultHelper.combineResults);
  }

  public refreshExtracts(ids: string[], extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) =>
          new RunExtractRefreshesOnWorkbooksRequest(idsBlock, extractType)
      ),
      ResultHelper.combineResults);
  }

  public createExtracts(ids: string[], dsIds: string[], encrypt: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
      new RunCreateExtractsOnWorkbooksRequest(idsBlock, dsIds, encrypt)
      ),
      ResultHelper.combineResults);
  }

  public encryptWorkbooks(ids: string[], actionType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnWorkbooksRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  public markRecentlyViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Workbook
    }));
  }

  public isWorkbookServerRefreshable(id: string): Promise<IIsWorkbookServerRefreshableResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new IIsWorkbookServerRefreshableRequest({ id: id }));
  }

  public cancelExtractCreationOnWorkbooks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new CancelExtractCreationRequest(ids, WorkbookContentType.name)
      ),
    ResultHelper.combineResults);
  }

  public getExtractDraftId(workbookId: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new GetExtractDraftIdRequest(workbookId));
  }
}

export const Workbooks = new WorkbooksResource();